import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useState } from "react";

// Icons
import {
  MdSupportAgent,
  MdDevices,
  MdWorkspacePremium,
  MdStorage,
  MdDns,
  MdCloud,
  MdBackup,
  MdMonitor,
  MdTipsAndUpdates,
} from "react-icons/md";

export default function Services() {
  const [hovered, setHovered] = useState(null);

  const services = [
    {
      title: "IT Helpdesk Support",
      desc: "Tier 1-3 support for hardware, software, network, and devices with remote & onsite assistance.",
      link: "/services/helpdesk-support",
      icon: <MdSupportAgent className="w-10 h-10 text-[#0f111a]" />,
    },
    {
      title: "Device & Endpoint Management",
      desc: "Setup, configuration, and management of laptops, mobile devices, and Chrome devices with MDM.",
      link: "/services/device-management",
      icon: <MdDevices className="w-10 h-10 text-[#0f111a]" />,
    },
    {
      title: "End-User Productivity Support",
      desc: "Assist users with software installations, configuration, troubleshooting, and Office 365 apps.",
      link: "/services/productivity-support",
      icon: <MdWorkspacePremium className="w-10 h-10 text-[#0f111a]" />,
    },
    {
      title: "Server & Virtualization Services",
      desc: "Deploy and manage servers, VMware ESXi & vCenter, Hyper-V, Linux/Windows servers, and virtual machines.",
      link: "/services/server-virtualization",
      icon: <MdStorage className="w-10 h-10 text-[#0f111a]" />,
    },
    {
      title: "Network & Security Solutions",
      desc: "Configure firewalls, VPNs, secure network access, monitoring, and micro-segmentation to protect IT environments.",
      link: "/services/network-security",
      icon: <MdDns className="w-10 h-10 text-[#0f111a]" />,
    },
    {
      title: "Cloud & Microsoft 365 Services",
      desc: "Manage Microsoft 365, Azure AD, SharePoint, Teams, OneDrive, and implement secure access policies.",
      link: "/services/cloud-m365",
      icon: <MdCloud className="w-10 h-10 text-[#0f111a]" />,
    },
    {
      title: "Backup & Disaster Recovery",
      desc: "Implement Veeam, Zerto, and other backup solutions with automated monitoring, testing, and reporting.",
      link: "/services/backup-recovery",
      icon: <MdBackup className="w-10 h-10 text-[#0f111a]" />,
    },
    {
      title: "Monitoring & Reporting",
      desc: "Proactively monitor servers, network, Active Directory, and backups; provide performance reports and alerts.",
      link: "/services/monitoring-reporting",
      icon: <MdMonitor className="w-10 h-10 text-[#0f111a]" />,
    },
    {
      title: "IT Consulting & Managed Services",
      desc: "Design scalable IT infrastructure, perform security audits, ensure compliance, and provide strategic guidance.",
      link: "/services/it-consulting",
      icon: <MdTipsAndUpdates className="w-10 h-10 text-[#0f111a]" />,
    },
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.1 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section className="relative bg-[#0f111a] py-20 px-6 md:px-16 text-white overflow-hidden">
      <div className="max-w-6xl mx-auto text-center">
        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-4xl md:text-5xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#00fff7] to-[#0077ff]"
        >
          Our Core Services
        </motion.h2>

        <p className="text-gray-300 max-w-3xl mx-auto text-lg md:text-xl mb-16 leading-relaxed">
          We combine innovation and technology to streamline your IT infrastructure and business performance.
        </p>

        {/* Services Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {services.map((service, i) => (
            <motion.div
              key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              variants={cardVariants}
              animate={{
                scale: hovered === i ? 1.05 : 1,
                boxShadow:
                  hovered === i
                    ? "0 15px 35px rgba(0, 255, 247, 0.25)"
                    : "0 4px 12px rgba(0, 0, 0, 0.18)",
              }}
              transition={{ type: "spring", stiffness: 120, damping: 15 }}
              className="p-8 bg-[#1a1f2b]/80 rounded-2xl border border-gray-800 hover:border-[#00fff7] cursor-pointer transition-all duration-300 backdrop-blur-sm flex flex-col justify-between min-h-[400px]"
            >
              <div className="flex flex-col items-center flex-grow">
                {/* Icon */}
                <motion.div
                  animate={{
                    boxShadow:
                      hovered === i
                        ? "0 0 20px #00fff7, 0 0 35px #0077ff"
                        : "0 0 0px transparent",
                  }}
                  transition={{ type: "spring", stiffness: 120, damping: 15 }}
                  className="mb-4 w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-tr from-[#00fff7] to-[#0077ff]"
                >
                  {service.icon}
                </motion.div>

                {/* Title */}
                <h3 className="text-xl font-semibold mb-3 text-[#00fff7] text-center">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-gray-300 leading-relaxed text-sm md:text-base text-center flex-grow">
                  {service.desc}
                </p>
              </div>

              {/* Learn More Button */}
              <Link to={service.link} className="w-full mt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#00fff7] to-[#0077ff] text-black font-semibold shadow-md transition"
                >
                  Learn More →
                </motion.button>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
